<script setup lang="ts">
import type { EditorialLookGridProps } from './EditorialLookGrid.props'
import { v4 as uuidv4 } from 'uuid'

const props = defineProps<EditorialLookGridProps>()

const { ts } = useI18n()

const isHovering = ref(false)
const LOOK_GRID_MODAL_ID = `look-grid-modal-${uuidv4()}`
const { openDialog } = useDialog(LOOK_GRID_MODAL_ID)

const selectedItem = ref(0)

const { visibleItems, itemsCount, createUIMediaObject, loadMoreItems } =
  useEditorialLookGrid(props)

const getItemLabel = (slide: any, index: number) => {
  let title: string | undefined = undefined

  switch (slide.titleType) {
    case 'fileName':
      title = slide.media.public_id
      break
    case 'string':
      title = slide.title ?? undefined
      break
    default:
      title = undefined
      break
  }

  const currentItem = index + 1

  return title ?? `${currentItem}/${itemsCount}`
}

const showMoreButtonLabel = computed(
  () => `${ts('plp.showMore')} (${visibleItems.value.length}/${itemsCount})`
)
const setSelectedImage = (index: number) => {
  selectedItem.value = index
  openDialog()
}
</script>

<template>
  <div class="editorial-look-grid positive-padding gap-8 py-4 md:py-6 lg:py-16">
    <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div
        v-for="(item, index) in visibleItems"
        :key="`${item.title ?? 'itemNumber'}-${index}`"
        class="grid gap-4"
      >
        <button
          aria-haspopup="dialog"
          :class="[isHovering ? 'custom-zoom-cursor-add' : '']"
          @click="setSelectedImage(index)"
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false"
        >
          <UIMedia :media="createUIMediaObject(item)" aspect-ratio="4/5" />
        </button>
        <span
          class="text-light-7 text-text-primary block overflow-hidden text-ellipsis text-nowrap text-right"
        >
          {{ getItemLabel(item, index) }}
        </span>
      </div>
    </div>
    <div class="m-auto flex flex-col items-center justify-center gap-2 py-6">
      <AtomsCta
        v-if="visibleItems.length < itemsCount"
        :icon-show-default="false"
        @click-handler="loadMoreItems()"
      >
        <template #label>
          {{ showMoreButtonLabel }}
        </template>
      </AtomsCta>
      <AtomsCta
        v-if="visibleItems.length < itemsCount"
        anatomy="ghost"
        :icon-show-default="false"
        @click-handler="loadMoreItems(true)"
      >
        <template #label>
          {{ $ts('plp.showAll') }}
        </template>
      </AtomsCta>
    </div>
    <OrganismsModal :id="LOOK_GRID_MODAL_ID" :is-editorial-look-grid="true">
      <template #body>
        <UISliderBasic
          :id="LOOK_GRID_MODAL_ID"
          :items="visibleItems"
          :selected-item="selectedItem"
          grap-cursor
          arrows
        >
          <template #slide="{ item, index }">
            <div
              class="flex shrink-0 grow-0 basis-full items-center justify-center"
            >
              <div class="flex h-[100dvh] flex-col justify-center gap-4">
                <UIZoomableImage
                  v-if="item.type === 'image'"
                  :src="item.media.secure_url"
                  :alt="item.alt ?? `image-${index}`"
                  :reset-zoom="0"
                  :is-gallery-slide-show="true"
                  class="positive-padding"
                />
                <UIFullScreenVideo
                  v-else-if="item.type === 'video'"
                  :src="item.media.secure_url"
                />
                <div
                  class="flex flex-col gap-2 px-2 md:m-auto md:w-[68vh] md:px-0"
                >
                  <UtilsMarkdown
                    :content="getItemLabel(item, index)"
                    is-preprocessed
                    container="span"
                    class="text-text-primary text-book-5 justify-end pb-4 text-end"
                  />
                </div>
              </div>
            </div>
          </template>
        </UISliderBasic>
      </template>
    </OrganismsModal>
  </div>
</template>

<style scoped lang="scss">
.custom-zoom-cursor-add {
  cursor:
    url('@design-system/icons/AddWBkg.svg') 24 24,
    auto;
}
</style>
